import { useRef, useState } from 'react';
import type { Boucle, Etat, Inventaire as InventaireData } from '../partage/api/types';
import { decompteApres, segments, type Issue } from './decompteDeLInventaire';
import { LigneInventaire } from './LigneInventaire';
import { RefusDeCycle } from '../moteur/conditions';
import { lireLeRefus } from '../projets';
import { useAuPouce } from '../partage/surface';
import { useEchap } from '../partage/useEchap';
import { motDeCompte, motsDuDossier } from '../partage/vocabulaire';
import './Inventaire.css';

/** Où en est « annuler ». `refusee` : la bulle a changé depuis la coche, rien à défaire. */
export type EtatAnnulation = 'repos' | 'en_cours' | 'refusee';

/**
 * L'inventaire
 *
 * Quand on valide un parent auquel il restait des enfants non faits, l'app fait l'inventaire
 * — **après coup, pas avant** : l'action est déjà passée, on ne demande pas la permission,
 * on montre ce qu'elle laisse derrière (SPEC §3).
 *
 * **Il se lit comme une liste à cocher** (ticket 28 de finition-v1). Quand on valide, on
 * veut vite passer à la suite : le cercle devant chaque étape la coche d'un clic, un second
 * clic la décoche. Pour ce qui ne sera pas fait maintenant ou qui va ailleurs :
 *   · **déplacer**          : elle n'était pas au bon endroit, et parfois pas dans le bon projet ;
 *   · **garder comme idée** : « En réflexion » (`sans_objet`). Elle sort du dénominateur de la
 *                             progression et ne retient rien, et redevient une tâche dès
 *                             qu'on lui donne un critère d'action ;
 *   · **supprimer**         : elle ne te concerne pas. Elle part avec ce qu'elle contient,
 *                             après la confirmation du ticket 10.
 * « Jeter » a disparu (ticket 18 de retours-testeurs, décision d'Igor du 2026-09-27).
 *
 * **Ne rien faire vaut garder**, et « garder » n'est donc plus un bouton (décision d'Igor du
 * 2026-09-25). L'inventaire est une offre, jamais un passage obligé : sinon on cesse de
 * cocher, et l'app meurt là. C'est pour ça que « tout garder » est du texte nu et que fermer
 * la carte est une issue légitime. Chaque geste s'écrit tout de suite : « c'est réglé » ne
 * fait que fermer.
 */
export function Inventaire({
  inventaire,
  onCocher,
  onDecocher,
  onIdee,
  onDeplacer,
  onAnnuler,
  annulation,
  onFermer,
}: {
  inventaire: InventaireData;
  /** Cocher une bulle restée : rend l'état qu'elle avait, pour pouvoir la décocher. */
  onCocher: (id: string) => Promise<Etat>;
  onDecocher: (id: string, etatAvant: Etat) => Promise<void>;
  /** Garder comme idée : « En réflexion ». */
  onIdee: (id: string) => void;
  /** Échoue si le serveur n'a rien écrit : la ligne revient alors comme avant le geste. */
  onDeplacer: (id: string, parentId: string | null) => Promise<void>;
  /** Défaire la coche : la bulle retrouve son état d'avant, et l'inventaire se ferme. */
  onAnnuler: () => void;
  annulation: EtatAnnulation;
  onFermer: () => void;
}) {
  const [issues, setIssues] = useState<Map<string, Issue>>(new Map());
  /** Ce qu'une ligne dit d'un déplacement qui n'a pas eu lieu, autre qu'une boucle. */
  const [dits, setDits] = useState<Map<string, string>>(new Map());
  /** Le déplacement refusé pour boucle : son panneau s'ouvre au-dessus de l'inventaire. */
  const [refusDeBoucle, setRefusDeBoucle] = useState<{ id: string; boucle: Boucle } | null>(null);
  const carte = useRef<HTMLDivElement>(null);
  const m = motsDuDossier(useAuPouce());

  function dire(id: string, mot: string | null) {
    setDits((avant) => {
      const apres = new Map(avant);
      if (mot) apres.set(id, mot);
      else apres.delete(id);
      return apres;
    });
  }

  function poser(id: string, issue: Issue | null) {
    setIssues((avant) => {
      const apres = new Map(avant);
      if (issue) apres.set(id, issue);
      else apres.delete(id);
      return apres;
    });
    // Un nouveau geste sur la ligne : ce qu'elle disait d'un échec ne vaut plus.
    if (issue) dire(id, null);
  }

  /*
   * Le déplacement se voit et compte au décompte avant la réponse du serveur. S'il n'a pas eu
   * lieu, la ligne revient comme avant le geste, le décompte aussi, et l'écran dit pourquoi :
   * une boucle ouvre son panneau, réduit à « Ne pas déplacer » ; le reste se dit sous la ligne
   * (chantier `deplacer`, ticket 03).
   */
  async function deplacerLigne(id: string, parentId: string | null, ou: 'dedans' | 'sortie') {
    poser(id, { sorte: 'deplacee', ou });
    try {
      await onDeplacer(id, parentId);
    } catch (erreur) {
      poser(id, null);
      const lu = lireLeRefus(erreur);
      if (lu.sorte === 'boucle') setRefusDeBoucle({ id, boucle: lu.boucle });
      else dire(id, lu.mot);
    }
  }

  /** « Ne pas déplacer » : le panneau se ferme, la main revient au « Déplacer » de la ligne. */
  function fermerLeRefus() {
    const id = refusDeBoucle?.id;
    setRefusDeBoucle(null);
    if (!id) return;
    carte.current
      ?.querySelector<HTMLButtonElement>(`[data-bulle="${CSS.escape(id)}"] [data-geste="deplacer"]`)
      ?.focus();
  }

  // Échap ferme le panneau de la boucle, et lui seul : ni l'inventaire ni ce qui est derrière.
  useEchap(refusDeBoucle !== null, fermerLeRefus, 'avant-tout');

  /*
   * La coche se voit tout de suite, et le décompte la suit avant même la réponse du
   * serveur : c'est ce qui fait qu'on passe vite à la ligne suivante. Si l'écriture échoue,
   * la ligne revient comme avant.
   */
  async function basculer(id: string) {
    const issue = issues.get(id);
    if (!issue) {
      poser(id, { sorte: 'cochee', etatAvant: null });
      try {
        poser(id, { sorte: 'cochee', etatAvant: await onCocher(id) });
      } catch {
        poser(id, null);
      }
    } else if (issue.sorte === 'cochee' && issue.etatAvant !== null) {
      poser(id, { sorte: 'decochage' });
      try {
        await onDecocher(id, issue.etatAvant);
        poser(id, null);
      } catch {
        poser(id, issue);
      }
    }
  }

  /*
   * Le décompte porte sur le dossier entier, comme la planche : « 9 sur 34 → 9 sur 32 ».
   * Le serveur donne où il en est après la coche, et ce que chaque geste lui changerait ;
   * l'écran n'additionne que ce que tu as choisi.
   */
  const { dossier } = inventaire;
  const apres = decompteApres(dossier, issues);
  const partantes = dossier.total - apres.total;
  const nb = inventaire.restait.length;
  const dejaDuMonde = inventaire.restait.some((b) => b.nbEnfants > 0);

  return (
    <>
      <div className="inventaire-voile" onClick={onFermer} aria-hidden="true" />

      <div className="inventaire" role="dialog" aria-label="ce qui restait dedans" ref={carte}>
        <header className="inventaire-tete">
          <div className="inventaire-coche" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 19 19" fill="none">
              <circle cx="9.5" cy="9.5" r="9.5" fill="var(--accent)" />
              <path d="M5.2 9.7l3 3 5.8-6.3" stroke="var(--surface)" strokeWidth="1.7" />
            </svg>
          </div>

          <div className="inventaire-dit">
            <div className="inventaire-fait">
              <h2>{inventaire.bulle.titre}</h2>
              <span>C’est fait</span>
            </div>
            <p className="inventaire-quand">
              <span className="inventaire-point" aria-hidden="true" />
              Coché à l’instant&nbsp;·{' '}
              {annulation === 'refusee' ? (
                <span>elle a changé depuis, rien à défaire</span>
              ) : (
                <button
                  type="button"
                  className="inventaire-annuler"
                  onClick={onAnnuler}
                  disabled={annulation === 'en_cours'}
                >
                  Annuler
                </button>
              )}
            </p>
          </div>

          <button
            type="button"
            className="inventaire-fermer"
            onClick={onFermer}
            aria-label="fermer l’inventaire"
          >
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
              <path d="M3.5 3.5l10 10M13.5 3.5l-10 10" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
        </header>

        {/* L'explication est dite une fois, ici, et plus sous chaque ligne. */}
        <p className="inventaire-amorce">
          {nb === 1 ? (
            <>
              Il restait une chose dedans. Coche-la si elle est déjà faite.{' '}
              <span>Sinon, ne rien faire, c’est la laisser où elle est.</span>
            </>
          ) : (
            <>
              Il restait {motDeCompte(nb)} dedans. Coche celles qui sont déjà faites.{' '}
              <span>Les autres, ne rien faire, c’est les laisser où elles sont.</span>
            </>
          )}
        </p>
        {dejaDuMonde && (
          <p className="inventaire-rappel">
            Les points disent ce qu’une étape contient déjà. La cocher ne coche qu’elle : ce
            qu’elle contient reste où il est.
          </p>
        )}

        <ul className="inventaire-liste">
          {inventaire.restait.map((bulle) => (
            <LigneInventaire
              key={bulle.id}
              bulle={bulle}
              issue={issues.get(bulle.id)}
              dit={dits.get(bulle.id)}
              dossierId={dossier.id}
              onBasculer={() => void basculer(bulle.id)}
              onIdee={() => {
                onIdee(bulle.id);
                poser(bulle.id, { sorte: 'idee' });
              }}
              onSupprimee={() => poser(bulle.id, { sorte: 'supprimee' })}
              onDeplacer={(parentId, ou) => void deplacerLigne(bulle.id, parentId, ou)}
            />
          ))}
        </ul>

        <div className="inventaire-decompte">
          <div className="inventaire-decompte-mesure">
            <p className="inventaire-decompte-titre">{m.decompte}</p>
            <div className="inventaire-barre">
              <div className="inventaire-segments" aria-hidden="true">
                {segments(apres, partantes).map((sorte, i) => (
                  <span key={i} className={`inventaire-segment est-${sorte}`} />
                ))}
              </div>
              <p className="inventaire-chiffres">
                {dossier.faites} sur {dossier.total}
                {(partantes > 0 || apres.faites !== dossier.faites) && (
                  <>
                    {' → '}
                    <span>
                      {apres.faites} sur {apres.total}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <p className="inventaire-decompte-glose">
            {partantes > 0
              ? 'Les segments en pointillé s’en vont. C’est le seul cas où la barre a le droit de raccourcir — et c’est toi qui viens de le décider, les yeux dessus.'
              : apres.faites > dossier.faites
                ? 'Ce que tu coches passe en acquis : la barre se remplit, elle ne raccourcit pas.'
                : 'Rien ne s’en va pour l’instant : ne rien faire vaut garder, et la barre ne bouge pas.'}
          </p>
        </div>

        <footer className="inventaire-pied">
          <p>
            Ne rien faire vaut <span>garder</span>. On ne te demande pas la permission&nbsp;:
            on te montre ce que ton geste laisse derrière.
          </p>
          <div className="inventaire-issues-pied">
            <button type="button" className="inventaire-tout-garder" onClick={onFermer}>
              Tout garder
            </button>
            <button className="bouton" onClick={onFermer}>
              C’est réglé
            </button>
          </div>
        </footer>
      </div>

      {/* Au-dessus de l'inventaire (z-index 60 contre 50), qui reste ouvert derrière. */}
      {refusDeBoucle && (
        <RefusDeCycle boucle={refusDeBoucle.boucle} geste="deplacement" onFermer={fermerLeRefus} />
      )}
    </>
  );
}
